// Member shell of the TC prototype. Composes vendored @ubm/ui-components sources only:
// - desktop: the SidebarRail block MOUNTED (its navigation rows, groups, Tooltip, active motion, "[" shortcut, refusal
//   port). Consumer CSS only hides its internal breadcrumb header (one flat header, ours, is rendered in its content slot)
//   and reserves the bottom-left room where the member Card and the profile (Avatar 36) sit. No vendored file is edited.
// - header: Button, CommandPalette inside the Dialog engine, NotificationCenter (Popover).
// - profile and mobile categories: Popover canon; theme: SegmentedControl; member card: Card.
// Data (person, notifications, commands) arrives through props: the shell holds no business fixture.
import { useCallback, useEffect, useId, useState, useSyncExternalStore, type CSSProperties, type ReactNode } from "react";
import { motion, useReducedMotionConfig } from "motion/react";
import {
  Activity, ArrowLeft, ChevronRight, Coins, GraduationCap, Handshake, LayoutDashboard, MessageCircle, MessagesSquare, Monitor, Moon,
  PanelLeft, Search, ShoppingBag, Sun, SquarePen, Target, Trophy, TrendingUp, UserRound, UsersRound, Wallet,
} from "lucide-react";
import { Avatar } from "../vendor/ubm-ui-components/src/components/avatar";
import { Button } from "../vendor/ubm-ui-components/src/components/button";
import { buttonCanonClassName } from "../vendor/ubm-ui-components/src/components/button/button-classes";
import { Card } from "../vendor/ubm-ui-components/src/components/card";
import { CommandPalette, type CommandItem } from "../vendor/ubm-ui-components/src/components/command-palette";
import { Root as DialogRoot, Portal as DialogPortal, Overlay as DialogOverlay, Content as DialogContent, Title as DialogTitle, dialogClassNames } from "../vendor/ubm-ui-components/src/components/dialog";
import { NotificationCenter, type NotificationItem } from "../vendor/ubm-ui-components/src/components/notification-center";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "../vendor/ubm-ui-components/src/components/popover";
import { SegmentedControl } from "../vendor/ubm-ui-components/src/components/segmented-control";
import { LiquidTabBar } from "../vendor/ubm-ui-components/src/components/liquid-tab-bar";
import { SidebarRail, type RailItem, type RailSection } from "../vendor/ubm-ui-components/src/components/sidebar-rail";
import { motionTokens } from "../vendor/ubm-ui-components/src/lib/motion-tokens";
import rail from "../vendor/ubm-ui-components/src/components/sidebar-rail/sidebar-rail.module.css";
import type { Route } from "./contracts";
import { pageChromeStore, MilesIcon, asset } from "./shared";

export type ThemePreference = "light" | "dark" | "system";

const subscribeHeaderLocation = (listener: () => void) => {
  window.addEventListener("hashchange", listener); window.addEventListener("popstate", listener);
  return () => { window.removeEventListener("hashchange", listener); window.removeEventListener("popstate", listener); };
};
export const routeTitles: Record<Route, string> = {
  home: "Tableau de bord",
  agir: "Agir",
  learn: "Apprendre",
  accompagnement: "Coaching",
  liberty: "Liberty Coach",
  community: "Communauté",
  team: "Messages",
  shop: "Boutique",
  account: "Mon compte",
  miles: "Mes Miles",
};

interface NavItem { route: Route; icon: ReactNode }
interface NavCategory { id: string; label: string; icon: ReactNode; items: NavItem[] }

const icon = (Glyph: typeof Target) => <Glyph size={18} strokeWidth={1.75} aria-hidden="true" />;

/** The navigation decided for the member space: one tree for the rail and the dock. Account and Miles live in the profile. */
const categories: NavCategory[] = [
  { id: "home", label: "Tableau de bord", icon: icon(LayoutDashboard), items: [{ route: "home", icon: icon(LayoutDashboard) }] },
  { id: "activities", label: "Mes activités", icon: icon(Activity), items: [
    { route: "agir", icon: icon(Trophy) },
    { route: "learn", icon: icon(GraduationCap) },
    { route: "accompagnement", icon: icon(Handshake) },
    { route: "liberty", icon: icon(TrendingUp) },
  ] },
  { id: "exchanges", label: "Échanges", icon: icon(MessagesSquare), items: [
    { route: "community", icon: icon(UsersRound) },
    { route: "team", icon: icon(MessageCircle) },
  ] },
  { id: "shop", label: "Boutique", icon: icon(ShoppingBag), items: [{ route: "shop", icon: icon(ShoppingBag) }] },
];

const categoryOf = (route: Route) => categories.find(category => category.items.some(item => item.route === route))?.id;

/** Palette entries for every destination of the shell; App appends its content entries. */
export const routeCommands: CommandItem[] = [...categories.flatMap(category => category.items), { route: "account" as Route, icon: icon(UserRound) }, { route: "miles" as Route, icon: icon(Wallet) }]
  .map(item => ({ id: `route:${item.route}`, label: routeTitles[item.route], group: "Aller à", icon: item.icon, keywords: [item.route] }));

export interface ShellPerson { name: string; status: string; avatar?: string; miles: number; pieces?: Record<"silver" | "gold" | "black" | "red", number>; /** Current learning level, e.g. "Niveau 1". */ level?: string }

export interface ShellProps {
  route: Route;
  navigate: (route: Route) => void;
  mobile: boolean;
  peekOpen: boolean;
  theme: ThemePreference;
  resolvedTheme: "light" | "dark";
  onThemeChange: (theme: ThemePreference) => void;
  person: ShellPerson;
  notifications: NotificationItem[];
  commands: CommandItem[];
  onCommand: (item: CommandItem) => void;
  /** Course reader active (Apprendre): the global sidebar, header and dock give way to the module's own chrome. The page
   *  stays mounted at the same place in the tree (hidden chrome, not a different tree), so the reader never remounts. */
  reader?: boolean;
  /** Opens Mon compte on its Achats section (#/account?section=purchases), provided by the root. */
  onOpenPurchases?: () => void;
  children: ReactNode;
}

const cx = (...names: (string | false | undefined)[]) => names.filter(Boolean).join(" ");
const COLLAPSED_KEY = "tc-proto-rail-collapsed";

/** The TC tree in SidebarRail terms: the item id is the route. Single-destination categories carry no section label. */
const railSections: RailSection[] = categories.map(category => ({
  id: category.id,
  label: category.items.length > 1 ? category.label : undefined,
  items: category.items.map(item => ({ id: item.route, label: routeTitles[item.route], icon: item.icon })),
}));
const railRoutes = new Set<string>(categories.flatMap(category => category.items.map(item => item.route)));

export function Shell({ route, navigate, mobile, peekOpen, theme, resolvedTheme, onThemeChange, person, notifications, commands, onCommand, reader = false, onOpenPurchases, children }: ShellProps) {
  const reduced = !!useReducedMotionConfig();
  const [collapsed, setCollapsedState] = useState(() => { try { return localStorage.getItem(COLLAPSED_KEY) === "1"; } catch { return false; } });
  const [paletteOpen, setPaletteOpen] = useState(false);
  const setCollapsed = useCallback((next: boolean) => {
    setCollapsedState(next);
    try { localStorage.setItem(COLLAPSED_KEY, next ? "1" : "0"); } catch { /* Private mode: the preference is not kept. */ }
  }, []);

  // Cmd/Ctrl + K opens the search. The rail keeps its own "[" shortcut (SidebarRail source).
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") { event.preventDefault(); setPaletteOpen(true); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const isCollapsed = !mobile && collapsed;
  const choose = (item: CommandItem) => { setPaletteOpen(false); onCommand(item); };

  // The rail reserves exactly the height of the bottom-left block (member card + profile), measured, never guessed.
  const [foot, setFoot] = useState<HTMLDivElement | null>(null);
  const [footHeight, setFootHeight] = useState(0);
  useEffect(() => {
    if (!foot || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => setFootHeight(Math.ceil(entry?.borderBoxSize?.[0]?.blockSize ?? foot.offsetHeight)));
    observer.observe(foot);
    return () => observer.disconnect();
  }, [foot]);

  const pageChrome = useSyncExternalStore(pageChromeStore.subscribe,pageChromeStore.getSnapshot,pageChromeStore.getSnapshot);
  const headerHash = useSyncExternalStore(subscribeHeaderLocation, () => window.location.hash, () => "");
  const coachingView = new URLSearchParams(headerHash.split("?")[1] ?? "").get("view");
  const headerTitle = route === "accompagnement" && coachingView === "consult" ? "Consultations"
    : route === "accompagnement" && coachingView === "genius" ? "Team Genius" : routeTitles[route];
  const libertySale = route === "liberty" && coachingView === "vente";
  const coachingChild = route === "accompagnement" && !!coachingView;
  const coachingSession = coachingChild && new URLSearchParams(headerHash.split("?")[1] ?? "").has("session");
  const parentLabel = libertySale ? "Liberty Coach" : coachingSession ? "Team Genius" : "Coaching";
  const goParent = () => { if (coachingSession) { window.history.pushState(null,"","#/accompagnement?view=genius"); window.dispatchEvent(new PopStateEvent("popstate")); } else navigate(libertySale ? "liberty" : "accompagnement"); };
  const header = <header className="tc-header">
    {!mobile && <Button variant="ghost" shape="icon" size="md" className="tc-header-icon" aria-label={isCollapsed ? "Déplier la navigation" : "Replier la navigation"} aria-expanded={!isCollapsed} aria-keyshortcuts="[" onClick={() => setCollapsed(!isCollapsed)}>
      <PanelLeft size={18} strokeWidth={1.75} aria-hidden="true" />
    </Button>}
    {mobile && !libertySale && !coachingChild && !pageChrome.back && <img className="tc-header-mark" src={asset("favicon-tc.svg")} alt="" width={28} height={28} />}
    {pageChrome.back && <Button variant="ghost" shape="icon" size="sm" aria-label="Retour aux conversations" onClick={pageChrome.back}><ArrowLeft size={20}/></Button>}
    {(libertySale || coachingChild) && <Button variant="ghost" shape="icon" size="sm" aria-label={`Retour à ${parentLabel}`} onClick={goParent}><ArrowLeft size={18}/></Button>}
    <h1 id="tc-title" className="tc-title">{pageChrome.title ?? (libertySale ? "Liberty Coach · Vente" : headerTitle)}</h1>
    <div className="tc-header-actions">
      {pageChrome.compose && <Button variant="ghost" shape="icon" size="md" aria-label={route === "community" ? "Écrire une publication" : "Nouveau message"} onClick={pageChrome.compose}><SquarePen size={20}/></Button>}
      <Button variant="ghost" shape="icon" size="md" className="tc-header-icon" aria-label="Rechercher" aria-haspopup="dialog" aria-keyshortcuts="Meta+K Control+K" onClick={() => setPaletteOpen(true)}>
        <Search size={18} strokeWidth={1.75} aria-hidden="true" />
      </Button>
      <NotificationCenter notifications={notifications} label="Notifications" />
      {mobile && <ProfileMenu person={person} route={route} navigate={navigate} theme={theme} onThemeChange={onThemeChange} compact side="bottom" onOpenPurchases={onOpenPurchases} />}
    </div>
  </header>;
  const frame = <div className="tc-frame">
    {reader ? null : header}
    <main id="tc-main" className={cx("tc-main", route === "team" && "tc-main--messages")} tabIndex={-1} aria-labelledby={reader ? undefined : "tc-title"} aria-label={reader ? routeTitles[route] : undefined}>{children}</main>
  </div>;

  return <div className="tc-shell" data-collapsed={isCollapsed || undefined} data-mobile={mobile || undefined} data-peek={peekOpen || undefined} data-reader={reader || undefined}
    style={{ "--tc-foot-height": `${footHeight}px` } as CSSProperties}>
    {mobile ? frame : <>
      <SidebarRail className="tc-rail-root" height="100dvh" sections={railSections}
        // Account and Miles are not rail destinations: an empty id selects no row (undefined would fall back to the first one).
        activeId={railRoutes.has(route) ? route : ""}
        onNavigate={(item: RailItem) => { navigate(item.id as Route); }}
        collapsed={isCollapsed} onCollapsedChange={setCollapsed}
        brand={<img className="tc-brand-logo" src={asset(resolvedTheme === "dark" ? "logo-teamcoaching-blanc.png" : "logo-team-coaching.png")} alt="Team Coaching" />}
        strings={{ nav: "Navigation principale", toggle: "Replier la navigation", opened: label => `${label} ouvert.`, refused: label => `${label} n’a pas pu s’ouvrir.`, breadcrumb: "Fil d’Ariane" }}>
        {frame}
      </SidebarRail>
      <motion.div ref={setFoot} className="tc-rail-foot" hidden={reader || undefined} initial={false} animate={{ width: isCollapsed ? 64 : 248 }} transition={reduced ? { duration: 0 } : motionTokens.spring.smooth}>
        <ProfileMenu person={person} route={route} navigate={navigate} theme={theme} onThemeChange={onThemeChange} compact={isCollapsed} side="top" onOpenPurchases={onOpenPurchases} />
      </motion.div>
    </>}

    {mobile && !reader && !(route === "team" && pageChrome.back) && !(route === "community" && peekOpen) && <MobileDock route={route} navigate={navigate} reduced={reduced} />}

    <DialogRoot open={paletteOpen} onOpenChange={setPaletteOpen}>
      <DialogPortal>
        <DialogOverlay className={cx(dialogClassNames.overlay, "tc-palette-scrim")} />
        <DialogContent className="tc-palette" aria-label="Rechercher dans mon espace">
          <DialogTitle className="tc-sr-only">Rechercher dans mon espace</DialogTitle>
          <CommandPalette items={commands} label="Rechercher" placeholder="Rechercher une page, un message, une actualité" autoFocus onSelect={choose} onClose={() => setPaletteOpen(false)} />
        </DialogContent>
      </DialogPortal>
    </DialogRoot>
  </div>;
}

/** Compact member card: brand identity, current learning level, Miles balance. Name/status live in the profile right below. */
function MemberCard({ person, onOpen }: { person: ShellPerson; onOpen: () => void }) {
  const miles = person.miles.toLocaleString("fr-FR");
  const label = `Carte membre Team Coaching${person.level ? `, ${person.level}` : ""}, ${miles} Miles disponibles`;
  return <Card className="tc-member-card" title="Team Coaching" description={person.level} aria-label={label} onClick={onOpen}
    meta={<span className="tc-member-balance" aria-label={`${miles} Miles`}><MilesIcon size={16} /><b>{miles}</b></span>}
    action={<Button variant="ghost" shape="icon" size="sm" aria-label="Voir mes Miles" onClick={event => { event.stopPropagation(); onOpen(); }}><ChevronRight size={16} strokeWidth={1.75} aria-hidden="true" /></Button>} />;
}

const themeOptions = [
  { value: "light", label: "Clair", accessory: <Sun size={14} strokeWidth={1.75} aria-hidden="true" /> },
  { value: "dark", label: "Sombre", accessory: <Moon size={14} strokeWidth={1.75} aria-hidden="true" /> },
  { value: "system", label: "Système", accessory: <Monitor size={14} strokeWidth={1.75} aria-hidden="true" /> },
];

interface ProfileMenuProps {
  person: ShellPerson;
  route: Route;
  navigate: (route: Route) => void;
  theme: ThemePreference;
  onThemeChange: (theme: ThemePreference) => void;
  compact: boolean;
  side: "top" | "bottom";
  onOpenPurchases?: () => void;
}

function ProfileMenu({ person, route, navigate, theme, onThemeChange, compact, side, onOpenPurchases }: ProfileMenuProps) {
  const [open, setOpen] = useState(false);
  const go = (next: Route) => { setOpen(false); navigate(next); };
  const here = route === "account" || route === "miles";
  const memberCard = side === "top" && !compact;
  return <Popover open={open} onOpenChange={setOpen}>
    <PopoverTrigger className={cx("tc-profile", compact && "tc-profile-compact", memberCard && "tc-member-profile")} data-active={here || undefined} aria-label={`Profil de ${person.name}`}>
      {memberCard ? <>
        <span className="tc-member-profile-top"><span className="tc-member-wordmark">TEAM<br/>COACHING</span><span className="tc-member-level">{person.level}</span></span>
        <span className="tc-member-wallet" aria-label="Mes pièces">{(["silver", "gold", "black", "red"] as const).map(kind => <span key={kind} className="tc-member-credit" aria-label={`${person.pieces?.[kind] ?? 0} pièces ${kind === "silver" ? "argent" : kind === "gold" ? "or" : kind === "black" ? "noires" : "rouges"}`}><span className={`ex-coin ex-coin-${kind} ex-coin-md`} aria-hidden="true"/><b>{person.pieces?.[kind] ?? 0}</b></span>)}</span>
        <span className="tc-member-profile-bottom"><Avatar name={person.name} src={person.avatar} size="md" /><span className="tc-member-person">{person.name}</span><span className="tc-member-miles" aria-label={`${person.miles.toLocaleString("fr-FR")} Miles`}><MilesIcon size={20}/>{person.miles.toLocaleString("fr-FR")}</span></span>
      </> : <><Avatar name={person.name} src={person.avatar} size="md" />
      {!compact && <span className="tc-profile-text"><span className="tc-profile-name">{person.name}</span><span className="tc-profile-status">{person.status}</span></span>}</>}
    </PopoverTrigger>
    <PopoverContent className="tc-profile-menu" side={side} align={side === "top" ? "start" : "end"} sideOffset={10}>
      <div className="tc-profile-head">
        <Avatar name={person.name} src={person.avatar} size="md" />
        <span className="tc-profile-text"><span className="tc-profile-name">{person.name}</span><span className="tc-profile-status">{person.miles.toLocaleString("fr-FR")} Miles disponibles</span></span>
      </div>
      <div className="tc-profile-wallet" aria-label="Mes pièces">{(["silver", "gold", "black", "red"] as const).map(kind => <span key={kind} className="tc-member-credit"><span className={`ex-coin ex-coin-${kind} ex-coin-md`} aria-hidden="true"/><b>{person.pieces?.[kind] ?? 0}</b></span>)}</div>
      <ul className={rail.list}>
        <li><button type="button" className={rail.row} data-active={route === "account" || undefined} aria-current={route === "account" ? "page" : undefined} onClick={() => go("account")}><span className={rail.icon}><UserRound size={18} strokeWidth={1.75} aria-hidden="true" /></span><span className={rail.label}>Mon compte</span></button></li>
        <li><button type="button" className={rail.row} data-active={route === "miles" || undefined} aria-current={route === "miles" ? "page" : undefined} onClick={() => go("miles")}><span className={rail.icon}><Wallet size={18} strokeWidth={1.75} aria-hidden="true" /></span><span className={rail.label}>Mes Miles</span></button></li>
        {onOpenPurchases && <li><button type="button" className={rail.row} onClick={() => { setOpen(false); onOpenPurchases(); }}><span className={rail.icon}><ShoppingBag size={18} strokeWidth={1.75} aria-hidden="true" /></span><span className={rail.label}>Mes achats</span></button></li>}
      </ul>
      <div className="tc-profile-theme">
        <span className="tc-profile-theme-label" aria-hidden="true">Thème</span>
        <SegmentedControl label="Thème" size="sm" options={themeOptions} value={theme} onValueChange={value => onThemeChange(value as ThemePreference)} />
      </div>
    </PopoverContent>
  </Popover>;
}

function MobileDock({ route, navigate }: { route: Route; navigate: (route: Route) => void; reduced: boolean }) {
  const [openCategory,setOpenCategory] = useState<string|null>(null);
  const category = categories.find(item=>item.id===openCategory);
  const go = (next:Route) => {setOpenCategory(null);navigate(next);};
  return <Popover open={!!category} onOpenChange={open=>{if(!open)setOpenCategory(null);}}>
    <PopoverAnchor asChild><div className="tc-ubm-dock-wrap" onClickCapture={event=>{const button=(event.target as HTMLElement).closest("button");const item=categories.find(c=>c.label===button?.getAttribute("aria-label"));if(!item)return;if(item.items.length===1)go(item.items[0]!.route);else setOpenCategory(openCategory===item.id?null:item.id);}}><LiquidTabBar label="Navigation principale" labels="active" tabs={categories.map(item=>({value:item.id,label:item.label,icon:item.icon}))} value={categoryOf(route)} onValueChange={()=>{}}/></div></PopoverAnchor>
    {category && <PopoverContent side="top" align="center" sideOffset={12} className="tc-dock-menu" aria-label={category.label}><p className={rail.sectionLabel}>{category.label}</p><ul className={rail.list}>{category.items.map(item=><li key={item.route}><button className={rail.row} aria-current={route===item.route?"page":undefined} onClick={()=>go(item.route)}><span className={rail.icon}>{item.icon}</span><span className={rail.label}>{routeTitles[item.route]}</span></button></li>)}</ul></PopoverContent>}
  </Popover>;
}
